import { constants } from '@/config';
import { CHANNELS, compareChannels } from '@/models/dashboards/channels.enum';
import { GRAPH_SCALES } from '@/models/dashboards/graph-scales.enum';

import { searchBrandMediaTypes } from '../library/constants';

export const pinterestTotalEngagementMetrics = [
  'TOTAL_CLOSEUPS',
  'TOTAL_LINK_CLICKS',
  'TOTAL_SAVES',
];

export const topLineMetricList = {
  tiktok:
    'NET_NEW_FOLLOWERS,TOTAL_VIDEO_VIEWS,TOTAL_ENGAGEMENTS,AVG_ENGAGEMENT_RATE,NUMBER_OF_POSTS',
  instagram:
    'NET_NEW_FOLLOWERS,VIEWS_VIDEO_REEL,ORGANIC_VIDEO_VIEWS,TOTAL_ENGAGEMENTS,IMPRESSIONS,VIEWS,AVG_ENGAGEMENT_RATE,NUMBER_OF_POSTS,NUMBER_OF_POSTS_ORGANIC',
  pinterest:
    'NET_NEW_FOLLOWERS,TOTAL_VIDEO_VIEWS,IMPRESSIONS_BY_POST,TOTAL_CLOSEUPS,TOTAL_LINK_CLICKS,TOTAL_SAVES,TOTAL_AVG_ENGAGEMENT_RATE,NUMBER_OF_POSTS',
  youtube: 'SUBSCRIBERS_NET_NEW,TOTAL_VIDEO_VIEWS,TOTAL_ENGAGEMENTS,NUMBER_OF_POSTS',
  facebook:
    'NET_NEW_FOLLOWERS,TOTAL_VIDEO_VIEWS,TOTAL_ENGAGEMENTS_POSTS_V2,PAID_AND_ORGANIC_VIEWS_BY_POST,AVG_ENGAGEMENT_RATE_V2,NUMBER_OF_POSTS',
  twitter:
    'NET_NEW_FOLLOWERS,ORGANIC_VIDEO_VIEWS,TOTAL_ENGAGEMENTS_POSTS,IMPRESSIONS_BY_POST,TOTAL_AVG_ENGAGEMENT_RATE,NUMBER_OF_POSTS',
  linkedin:
    'NET_NEW_FOLLOWERS,VIDEO_VIEWS_BY_POST,ENGAGEMENTS,IMPRESSIONS,AVG_ENGAGEMENT_RATE,NUMBER_OF_POSTS',
};

export const tooltips = {
  videoViews: {
    title: 'Video Views',
    content:
      'The sum of video views of all posts published for all channels during the reporting period.',
  },
  netNewFollowers: {
    title: 'Net New Followers',
    content: 'The net new followers accumulated for all channels during the reporting period.',
    footer: 'Net New Followers = Total Followers - Followers Lost',
  },
  impressions: {
    title: 'Impressions',
    content: 'The number of times your posts were viewed or displayed during the reporting period.',
  },
  totalEngagements: {
    title: 'Total Engagements',
    content:
      'The total engagements from all posts published for all channels during the reporting period.',
  },
  avgEngagementRate: {
    title: 'Avg. Engagement Rate',
    content:
      'The average of the average engagement rates for all channels during the reporting period.',
  },
  totalTSI: {
    title: 'Total Social Impact (TSI)',
    content:
      'The combined measure of your overall social footprint across all connected channels during the reporting period.',
  },
  topTweets: 'The Posts published during the reporting period that received the most impressions.',
  topPins: 'The Pins published during the reporting period that received the most impressions.',
  numberOfPosts: {
    title: 'Number Of Posts',
    content: 'The number of posts published for all channels during the reporting period.',
  },
};

export const avgEngagementRate = 'AVG_ENGAGEMENT_RATE_V3';

export const topLineTotalAggregationType = 'TOTAL';

export const toplineValues = [
  {
    valueName: 'videoViews',
    label: 'Video Views',
    isPercent: false,
    tooltip: tooltips.videoViews,
    metric: 'VIDEO_VIEWS',
  },
  {
    valueName: 'netNewFollowers',
    label: 'Net New Followers',
    isPercent: false,
    tooltip: tooltips.netNewFollowers,
    metric: 'NET_NEW_FOLLOWERS',
  },
  {
    valueName: 'impressions',
    label: 'Impressions',
    isPercent: false,
    tooltip: tooltips.impressions,
    metric: 'IMPRESSIONS',
  },
  {
    valueName: 'totalEngagements',
    label: 'Total Engagements',
    isPercent: false,
    tooltip: tooltips.totalEngagements,
    metric: 'TOTAL_ENGAGEMENTS',
  },
  {
    valueName: 'avgEngagementRate',
    label: 'Avg. Engagement Rate',
    isPercent: true,
    tooltip: tooltips.avgEngagementRate,
    metric: 'AVG_ENGAGEMENT_RATE',
  },
  {
    valueName: 'numberOfPosts',
    label: 'Number Of Posts',
    isPercent: false,
    tooltip: tooltips.numberOfPosts,
    metric: 'NUMBER_OF_POSTS',
  },
  {
    valueName: 'totalTSI',
    label: 'Total Social Impact (TSI)',
    isPercent: false,
    tooltip: tooltips.totalTSI,
    metric: 'TOTAL_TSI',
    hideBreakdown: true,
  },
];

export const metricBreakdownTooltips = {
  INSTAGRAM: {
    totalEngagements:
      'Engagements = (Likes + Saves + Comments + Shares) - (Unlikes + Unsaves + Deleted Comments)',
    avgEngagementRate: 'Engagement Rate = Engagements / Followers',
  },
  FACEBOOK: {
    totalEngagements: 'Engagements = Reactions + Shares + Comments',
    avgEngagementRate: 'Engagement Rate = Engagements / Views',
  },
  TIKTOK: {
    totalEngagements: 'Total Engagements = Likes + Comments + Shares',
    avgEngagementRate: 'Engagement Rate = Total Engagements / Reach',
  },
  PINTEREST: {
    totalEngagements: 'Engagements = Outbound Clicks + Saves + Pin Clicks',
    avgEngagementRate: 'Engagement Rate = Engagements / Impressions',
  },
  TWITTER: {
    totalEngagements: 'Total Engagements = Reposts + Replies + Likes + Link Clicks',
    avgEngagementRate: 'Engagement Rate = Total Engagements / Impressions',
  },
  YOUTUBE: { totalEngagements: 'Total Engagements = Likes + Dislikes + Comments + Shares' },
  LINKEDIN: {
    avgEngagementRate: 'Engagement Rate = Total Engagements / Impressions',
  },
};

export const verticalMediaPanelCopy = {
  topInstagramStories: {
    title: 'Top Performing Instagram Story Frames',
    tooltip:
      'The Instagram Story Frames published during the reporting period that received the most impressions',
    channel: CHANNELS.INSTAGRAM_STORIES.value,
    emptyMessage: 'No Story Frames found during this reporting period',
  },
  topInstagramVideos: {
    title: 'Top Performing Instagram Videos',
    tooltip:
      'The Instagram videos published during the reporting period that received the most video views',
    channel: CHANNELS.INSTAGRAM.value,
    emptyMessage: 'No Instagram videos found during this reporting period',
  },
  mostViewedVideos: {
    title: 'Most Viewed Videos',
    tooltip:
      'Top performing videos across all channels, sorted by video views or impressions if applicable',
    emptyMessage: 'No videos found during this reporting period',
  },
  topTiktokVideos: {
    title: 'Top Performing TikTok Posts',
    tooltip:
      'The TikTok posts published during the reporting period that received the most video views',
    channel: CHANNELS.TIKTOK.value,
    emptyMessage: 'No TikTok posts found during this reporting period',
  },
  topVisionPredictedUGCVideos: {
    title: 'Top Vision Predicted UGC Videos',
    tooltip:
      'Vision predicted top UGC that either @mentioned or phototagged you during the reporting period, sorted by video views',
    emptyMessage: 'No videos found during this reporting period',
  },
  mostEntertainingVideos: {
    title: 'Most Entertaining Videos',
    tooltip:
      'Most entertaining videos across Instagram and TikTok sorted by the Entertainment Score',
    emptyMessage: 'No videos found during this reporting period',
  },
};

export const mediaPanelCopy = {
  topYoutubeVideos: {
    title: 'Top Performing YouTube Videos',
    tooltip:
      'The YouTube videos published during the reporting period that received the most video views.',
    channel: CHANNELS.YOUTUBE.value,
    emptyMessage: 'No videos found during this time range',
  },
};

export const orderedReportChannelList = [
  constants.TIKTOK,
  constants.INSTAGRAM,
  constants.FACEBOOK,
  constants.PINTEREST,
  constants.TWITTER,
  constants.YOUTUBE,
  constants.LINKEDIN,
].sort(compareChannels);

export const orderedTSIChannelList = [
  constants.TIKTOK,
  constants.INSTAGRAM,
  constants.INSTAGRAM_STORIES,
  constants.FACEBOOK,
  constants.PINTEREST,
  constants.TWITTER,
  constants.YOUTUBE,
  constants.LINKEDIN,
].sort(compareChannels);

export const graphScale = GRAPH_SCALES.DAILY.value;

export const graphMetrics = {
  AVG_ENGAGEMENT_RATE: {
    value: 'AVG_ENGAGEMENT_RATE',
    text: 'Avg. Engagement Rate',
  },
  AVG_ENGAGEMENT_RATE_V2: {
    value: 'AVG_ENGAGEMENT_RATE_V2',
    text: 'Avg. Engagement Rate',
  },
  // Comparable metrics only
  AVG_ENGAGEMENT_RATE_V3: {
    value: 'AVG_ENGAGEMENT_RATE_V3',
    text: 'Avg. Engagement Rate',
  },
  TOTAL_VIDEO_VIEWS: {
    value: 'TOTAL_VIDEO_VIEWS',
    text: 'Video Views',
  },
  ORGANIC_VIDEO_VIEWS: {
    value: 'ORGANIC_VIDEO_VIEWS',
    text: 'Video Views',
  },
  PAID_AND_ORGANIC_VIDEO_VIEWS: {
    value: 'PAID_AND_ORGANIC_VIDEO_VIEWS',
    text: 'Video Views',
  },
  VIEWS: {
    value: 'VIEWS',
    text: 'Video Views',
  },
  VIEWS_VIDEO_REEL: {
    value: 'VIEWS_VIDEO_REEL',
    text: 'Video Views',
  },
  TOTAL_TSI: {
    value: 'TOTAL_TSI',
    text: 'Total Social Impact (TSI)',
  },
};

export const mostEntertainingVideosBrandMediaTypes = [
  searchBrandMediaTypes.INSTAGRAM_OWNED,
  searchBrandMediaTypes.INSTAGRAM_OWNED_IGTV,
  searchBrandMediaTypes.TIKTOK_OWNED,
];

export const mostViewedVideosBrandMediaTypes = [
  searchBrandMediaTypes.INSTAGRAM_OWNED,
  searchBrandMediaTypes.INSTAGRAM_OWNED_IGTV,
  searchBrandMediaTypes.TIKTOK_OWNED,
  searchBrandMediaTypes.FACEBOOK_OWNED,
  searchBrandMediaTypes.PINTEREST_OWNED,
  searchBrandMediaTypes.TWITTER_OWNED,
  searchBrandMediaTypes.YOUTUBE_OWNED,
  searchBrandMediaTypes.LINKEDIN_OWNED,
];

export const CHANNELS_IN_ORDER = [
  'TIKTOK',
  'INSTAGRAM',
  'FACEBOOK',
  'PINTEREST',
  'TWITTER',
  'YOUTUBE',
  'LINKEDIN',
];

export const LANDING_PAGE_FILTER_DETAILS = Object.freeze({
  LANDING_PAGE: {
    scope: 'landingPage',
    options: {
      reportingPeriod: {},
      comparisonPeriod: {
        label: 'Comparison Period',
        required: true,
      },
    },
    default: {
      reportingPeriod: {
        preset: 'LAST_7_DAYS',
      },
      comparisonPeriod: {
        preset: 'PREVIOUS_7_DAYS',
      },
    },
    queryParamMapping: {
      'reportingPeriod.range.start': { queryParamName: 'startDate', type: 'date' },
      'reportingPeriod.range.end': { queryParamName: 'endDate', type: 'date' },
      'reportingPeriod.preset': { queryParamName: 'reportingPreset', type: 'string' },
      'comparisonPeriod.range.start': { queryParamName: 'comparisonStartDate', type: 'date' },
      'comparisonPeriod.range.end': { queryParamName: 'comparisonEndDate', type: 'date' },
      'comparisonPeriod.preset': { queryParamName: 'comparisonPreset', type: 'string' },
    },
  },
});

export const graphTooltips = {
  TOTAL_VIDEO_VIEWS: {
    title: 'Video Views',
    content:
      'The sum of video views of all posts published for all channels during the reporting period.',
  },
  AVG_ENGAGEMENT_RATE: {
    title: 'Avg. Engagement Rate',
    content:
      'The average of the average engagement rates for all channels during the reporting period.',
  },
  TOTAL_TSI: {
    title: 'Total Social Impact (TSI)',
    content: 'The combined measure of your overall social footprint across all connected channels',
  },
};

export const tsiGraphTooltips = {
  LINE_CHART: {
    title: 'Total Social Impact (TSI)',
    content:
      'TSI shows how your social footprint grows over time. This view connects posting frequency and performance across channels to overall momentum.',
  },
  DOUGHNUT_CHART: {
    title: 'Total Social Impact (TSI)',
    content:
      'See how each connected channel contributes to your total TSI. Use this report to compare impact and decide where to focus next.',
  },
};
